import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX, Play } from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { useSiteConfig } from "../context/SiteConfigContext";

// Fire-and-forget engagement ping; "play" is counted once per page load.
const track = (type) => api.post("/video/hero/event", { type }).catch(() => {});

/* Landing-page video. Renders nothing unless an admin uploaded a video and
   turned it on (Admin → Video). Autoplays muted so the pitch starts instantly;
   one tap turns the sound on and restarts from the top. */
export default function HeroVideo({ variant = "classic" }) {
    const { heroVideo } = useSiteConfig();
    const ref = useRef(null);
    const playedOnce = useRef(false);
    const [muted, setMuted] = useState(true);
    const [paused, setPaused] = useState(false);
    if (!heroVideo?.enabled || !heroVideo?.url) return null;

    const onPlay = () => {
        setPaused(false);
        if (!playedOnce.current) { playedOnce.current = true; track("play"); }
    };
    const toggleSound = () => {
        const v = ref.current;
        if (!v) return;
        if (muted) { v.muted = false; v.currentTime = 0; v.play().catch(() => {}); setMuted(false); track("sound"); }
        else { v.muted = true; setMuted(true); }
    };
    const frame = variant === "editorial"
        ? "rounded-[28px] border border-amber-900/20 shadow-[0_30px_80px_rgba(0,0,0,0.25)]"
        : "rounded-3xl border border-white/10 shadow-[0_40px_120px_rgba(0,0,0,0.55)]";

    return (
        <motion.section
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
            className="relative z-10 mx-auto max-w-7xl px-5 sm:px-10 pb-12" data-testid="hero-video-section"
        >
            <div className={`group relative overflow-hidden bg-black ${frame}`}>
                <video
                    ref={ref}
                    src={mediaUrl(heroVideo.url)}
                    poster={heroVideo.poster ? mediaUrl(heroVideo.poster) : undefined}
                    className="aspect-video w-full object-cover"
                    autoPlay muted loop playsInline preload="metadata"
                    onPause={() => setPaused(true)} onPlay={onPlay}
                    onClick={() => { const v = ref.current; if (!v) return; v.paused ? v.play() : v.pause(); }}
                    data-testid="hero-video"
                />
                {paused && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/30">
                        <div className="rounded-full bg-orange-500 p-5 text-black shadow-xl"><Play className="h-8 w-8 fill-current" /></div>
                    </div>
                )}
                <button
                    type="button" onClick={toggleSound} data-testid="hero-video-sound"
                    className="absolute bottom-4 right-4 flex items-center gap-2 rounded-full border border-white/20 bg-black/60 px-4 py-2 text-sm font-bold text-white backdrop-blur transition-colors hover:bg-black/80"
                >
                    {muted ? <><VolumeX className="h-4 w-4" /> Tap for sound</> : <><Volume2 className="h-4 w-4 text-orange-400" /> Sound on</>}
                </button>
            </div>
        </motion.section>
    );
}
